import axios from 'axios';

// ───────────── Notificaciones push en este dispositivo (v12.5.6) ─────────────
// Cada persona las activa una vez por dispositivo (celular con la web app instalada,
// o la PC) desde "Mi perfil". El navegador entrega una suscripción que se guarda en
// el servidor (/api/push); desde ahí el sistema manda los avisos y el service worker
// (public/service-worker.js) los muestra aunque la app esté cerrada.

export type EstadoPush =
  | 'activo'            // este dispositivo recibe avisos
  | 'inactivo'          // se pueden activar
  | 'bloqueado'         // el permiso está denegado en el navegador o el celular
  | 'ios-sin-instalar'  // iPhone/iPad abierto en Safari: hay que agregarlo a inicio
  | 'no-soportado';

const RUTA_SW = '/service-worker.js';

/** Motivos por los que no se pudo activar, para mostrarle algo claro a la persona. */
export class ErrorPush extends Error {
  motivo: 'bloqueado' | 'sin-permiso' | 'inactivo';
  constructor(motivo: 'bloqueado' | 'sin-permiso' | 'inactivo') {
    super(motivo);
    this.motivo = motivo;
  }
}

const esIPad = () => /ipad/i.test(navigator.userAgent || '') || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const esIOS = (): boolean => /iphone|ipod/i.test(navigator.userAgent || '') || esIPad();

/** ¿Está abierto como app instalada (desde el ícono de la pantalla de inicio)? */
export const estaInstalada = (): boolean =>
  (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) ||
  (navigator as any).standalone === true;

const soportaPush = (): boolean =>
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const aBytes = (base64url: string): Uint8Array => {
  const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4);
  const crudo = atob(b64);
  const bytes = new Uint8Array(crudo.length);
  for (let i = 0; i < crudo.length; i++) bytes[i] = crudo.charCodeAt(i);
  return bytes;
};

const aBase64url = (buffer: ArrayBuffer): string => {
  let crudo = '';
  new Uint8Array(buffer).forEach((b) => { crudo += String.fromCharCode(b); });
  return btoa(crudo).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

/** "iPhone · Safari": para reconocer el dispositivo en el servidor. */
export const nombreDispositivo = (): string => {
  const ua = navigator.userAgent || '';
  const equipo = /iphone|ipod/i.test(ua) ? 'iPhone'
    : esIPad() ? 'iPad'
    : /android/i.test(ua) ? 'Android'
    : /windows/i.test(ua) ? 'Windows'
    : /mac os/i.test(ua) ? 'Mac'
    : /linux/i.test(ua) ? 'Linux'
    : 'Dispositivo';
  const navegador = /edg\//i.test(ua) ? 'Edge'
    : /firefox|fxios/i.test(ua) ? 'Firefox'
    : /samsungbrowser/i.test(ua) ? 'Samsung'
    : /chrome|crios/i.test(ua) ? 'Chrome'
    : /safari/i.test(ua) ? 'Safari'
    : 'navegador';
  return `${equipo} · ${navegador}`;
};

const suscripcionActual = async (): Promise<PushSubscription | null> => {
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
};

export const leerEstadoPush = async (): Promise<EstadoPush> => {
  if (!soportaPush()) return esIOS() && !estaInstalada() ? 'ios-sin-instalar' : 'no-soportado';
  if (Notification.permission === 'denied') return 'bloqueado';
  try {
    return Notification.permission === 'granted' && (await suscripcionActual()) ? 'activo' : 'inactivo';
  } catch {
    return 'inactivo';
  }
};

// Una suscripción hecha con otra clave del servidor ya no sirve para recibir
const mismaClave = (sub: PushSubscription, clave: string): boolean => {
  const actual = sub.options?.applicationServerKey;
  return !actual || aBase64url(actual) === clave;
};

/** Activa las notificaciones en este dispositivo. Tiene que llamarse desde el toque de un botón. */
export const activarPush = async (): Promise<void> => {
  // El permiso se pide antes que nada, sin esperas previas: Safari exige que salga directo del toque
  const permiso = await Notification.requestPermission();
  if (permiso !== 'granted') throw new ErrorPush(permiso === 'denied' ? 'bloqueado' : 'sin-permiso');
  const reg = await navigator.serviceWorker.register(RUTA_SW);
  await navigator.serviceWorker.ready;
  const { data } = await axios.get('/api/push/clave');
  let sub = await reg.pushManager.getSubscription();
  if (sub && !mismaClave(sub, data.clave)) {
    await sub.unsubscribe().catch(() => {});
    sub = null;
  }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: aBytes(data.clave) });
  await axios.post('/api/push/suscripcion', { suscripcion: sub.toJSON(), dispositivo: nombreDispositivo() });
};

export const desactivarPush = async (): Promise<void> => {
  const sub = await suscripcionActual();
  if (!sub) return;
  await axios.delete('/api/push/suscripcion', { data: { endpoint: sub.endpoint } }).catch(() => {});
  await sub.unsubscribe().catch(() => {});
};

/** Manda una notificación de prueba a este dispositivo; con `demora` sale a los N segundos. */
export const probarPush = async (demora = 0): Promise<void> => {
  const sub = await suscripcionActual();
  if (!sub) throw new ErrorPush('inactivo');
  await axios.post('/api/push/prueba', { endpoint: sub.endpoint, demora });
};

/**
 * Al abrir el sistema: si este dispositivo ya las tiene activadas, queda a nombre de
 * quien inició sesión (y el servidor se entera si el navegador renovó la suscripción).
 */
export const sincronizarPush = async (): Promise<void> => {
  try {
    if (!soportaPush() || Notification.permission !== 'granted') return;
    const sub = await suscripcionActual();
    if (!sub) return;
    await axios.post('/api/push/suscripcion', { suscripcion: sub.toJSON(), dispositivo: nombreDispositivo() });
  } catch {
    /* se reintenta la próxima vez que se abra */
  }
};

/** Al cerrar sesión, este dispositivo deja de recibir los avisos de esa persona. */
export const desvincularPushAlSalir = (token: string | null): void => {
  try {
    if (!token || !soportaPush()) return;
    suscripcionActual()
      .then((sub) => {
        if (!sub) return undefined;
        return axios.delete('/api/push/suscripcion', { data: { endpoint: sub.endpoint }, headers: { Authorization: `Bearer ${token}` } });
      })
      .catch(() => {});
  } catch {
    /* cerrar sesión no puede fallar por esto */
  }
};

/**
 * Si tocan un aviso con el sistema ya abierto, el service worker avisa a qué pantalla
 * ir. Devuelve la función para dejar de escuchar.
 */
export const alTocarAvisoPush = (ir: (url: string) => void): (() => void) => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};
  const escuchar = (e: MessageEvent) => {
    const url = e.data?.tipo === 'rocam-abrir' ? String(e.data.url || '') : '';
    if (url.startsWith('/') && !url.startsWith('//')) ir(url);
  };
  navigator.serviceWorker.addEventListener('message', escuchar);
  return () => navigator.serviceWorker.removeEventListener('message', escuchar);
};
